import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { publicApi } from "../../api";
import StepChoice from "../../components/StepChoice";
import StepLookup from "../../components/StepLookup";
import StepLicence from "../../components/StepLicence";
import StepSelfie from "../../components/StepSelfie";
import StepDetails from "../../components/StepDetails";
import StepCommsPrefs from "../../components/StepCommsPrefs";
import StepTerms from "../../components/StepTerms";
import StepPlan from "../../components/StepPlan";
import StepPayment from "../../components/StepPayment";
import StepConfirmation from "../../components/StepConfirmation";
import { isSlotVisible, slotCss } from "../../utils/signupTextStyle";
import { resolveBenefits, BenefitIcon } from "../../utils/signupBenefits";
import { resolveLayout, shellClasses } from "../../utils/signupLayout";
import "../../components/signup.css";

// Internal step machine:
//   0 = choice            (not shown in progress bar)
//   1 = lookup            (existing-member branch only)
//   2 = details
//   3 = comms-prefs       (only when the venue has channels OR agm_enabled)
//   4 = terms             (only when venue.terms_required)
//   5 = plan
//   6 = payment
//   7 = done
//
// Comms and Terms steps are skipped entirely when their venue flags
// aren't set — the bar collapses to match what's actually rendered.
const STEP_DEFS = [
  { key: "start",   label: "Start"          },
  { key: "details", label: "Details"        },
  { key: "comms",   label: "Comms"          },
  { key: "terms",   label: "Terms"          },
  { key: "plan",    label: "Plan"           },
  { key: "payment", label: "Payment"        },
  { key: "done",    label: "Done"           },
];

function buildProgressSteps({ commsEnabled, termsEnabled }) {
  return STEP_DEFS.filter((s) => {
    if (s.key === "comms" && !commsEnabled) return false;
    if (s.key === "terms" && !termsEnabled) return false;
    return true;
  });
}

// Maps the internal step number to its slot in the filtered
// progress-bar list. Steps that are skipped for this venue are simply
// omitted from STEP_DEFS, so we map step → key → list index.
function progressIndex(step, progressSteps) {
  const stepKey =
    step <= 1 ? "start"
    : step === 2 ? "details"
    : step === 3 ? "comms"
    : step === 4 ? "terms"
    : step === 5 ? "plan"
    : step === 6 ? "payment"
    : "done";
  const idx = progressSteps.findIndex((s) => s.key === stepKey);
  return idx === -1 ? 0 : idx;
}

/* ── Colour contrast helpers ──────────────────────────────────────────
 * The signup card is always a light/white surface, but venue branding
 * colours are admin-controlled and can be chosen for a dark theme (e.g. a
 * near-white text_color, or a mid-tone primary with white button text).
 * Applied verbatim that produces invisible headings and washed-out CTAs, so
 * we guarantee a readable text colour on each surface rather than trusting
 * the raw config. */
function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function relLuminance(rgb) {
  const f = (c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(rgb[0]) + 0.7152 * f(rgb[1]) + 0.0722 * f(rgb[2]);
}
function contrastRatio(hexA, hexB) {
  const a = hexToRgb(hexA), b = hexToRgb(hexB);
  if (!a || !b) return 21;
  const L1 = relLuminance(a), L2 = relLuminance(b);
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
}
const DARK_INK = "#0f172a";
const LIGHT_INK = "#ffffff";
// Best of dark/light ink for a given background.
function autoInk(bg) {
  return contrastRatio(bg, DARK_INK) >= contrastRatio(bg, LIGHT_INK) ? DARK_INK : LIGHT_INK;
}
// Use the preferred colour only if it clears `min` contrast on `bg`; otherwise
// fall back to the higher-contrast ink so text is never illegible.
function readableOn(bg, preferred, min) {
  if (preferred && contrastRatio(bg, preferred) >= min) return preferred;
  return autoInk(bg);
}
function rgbToHex(rgb) {
  return "#" + rgb.map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, "0")).join("");
}
// A contrast-safe version of `hex` for use as FOREGROUND (text/icon/border) on
// `bg`. Keeps the brand hue but darkens it toward black until it clears `min`
// contrast, so a pale/bright brand colour stays legible instead of washing out.
function inkOn(bg, hex, min = 4.5) {
  const base = hexToRgb(hex);
  if (!base) return autoInk(bg);
  if (contrastRatio(bg, hex) >= min) return hex;
  for (let t = 0.15; t <= 1.0001; t += 0.15) {
    const c = rgbToHex(base.map((ch) => ch * (1 - t)));
    if (contrastRatio(bg, c) >= min) return c;
  }
  return autoInk(bg);
}

export default function VenueSignup() {
  const { slug } = useParams();
  const [venue, setVenue]               = useState(null);
  const isBrandPreview = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("brandPreview");
  const [stripePromise, setStripePromise] = useState(null);
  const [products, setProducts]         = useState([]);
  const [step, setStep]                 = useState(0);
  const [formData, setFormData]         = useState({});
  // Marketing attribution (UTM params + click IDs + referrer), captured once on
  // first render from the landing URL so it survives step navigation through to
  // register-member. Empty object when the link carried no tracking.
  const [tracking] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const g = (k) => { const v = p.get(k); return v ? v.slice(0, 500) : undefined; };
      const t = {
        utm_source: g("utm_source"), utm_medium: g("utm_medium"), utm_campaign: g("utm_campaign"),
        utm_term: g("utm_term"), utm_content: g("utm_content"),
        gclid: g("gclid"), fbclid: g("fbclid"),
        referrer: (document.referrer || "").slice(0, 500) || undefined,
      };
      return Object.fromEntries(Object.entries(t).filter(([, v]) => v));
    } catch { return {}; }
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [result, setResult]             = useState(null);
  const [isExisting, setIsExisting]     = useState(false);
  const [existingMemberNumber, setExistingMemberNumber] = useState(null);
  const [loadingVenue, setLoadingVenue] = useState(true);
  const [error, setError]               = useState("");
  // T&Cs acceptance payload — captured at the Terms step and shipped
  // to the backend on register-member. Null when the venue doesn't
  // require acknowledgement (the Terms step is skipped entirely).
  const [termsAcceptance, setTermsAcceptance] = useState(null);
  // Comms-prefs payload — { marketingOptionIds, agmAcknowledged,
  // agmVersion }. Null until captured; non-null even when the comms
  // step is skipped so register-member always sees a deterministic
  // shape downstream.
  const [commsPrefs, setCommsPrefs] = useState(null);
  // Available marketing channels for this venue, loaded alongside the
  // venue info. Drives whether the Comms step is shown — combined with
  // venue.agm_enabled, an empty list AND no AGM means we skip entirely.
  const [marketingOptions, setMarketingOptions] = useState([]);
  // New-join duplicate guard — when the venue opts in, the Details step
  // runs a name+DOB check before payment. Non-null holds the backend's
  // { message } and swaps the Details step for an "already a member"
  // notice directing the member to staff (a new signup is never funneled
  // into renewal, regardless of whether the venue offers it).
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  // FOXID licence capture — `licenceDone` flips true once the member has
  // scanned or chosen to enter details manually, swapping the in-place capture
  // step (rendered at step 2) for the Details form. `scanRef` links the stored
  // licence to the member on register.
  const [licenceDone, setLicenceDone] = useState(false);
  const [scanRef, setScanRef] = useState(null);

  // Per-venue web-analytics pixels (B): when the venue has configured a GA4
  // Measurement ID and/or a Meta Pixel ID, inject that tag once the venue loads
  // and fire a page_view. The conversion event fires on signup success below.
  // NOTE: these load third-party trackers + set cookies — the venue is
  // responsible for its own cookie-consent obligations.
  useEffect(() => {
    if (!venue) return;
    const ga = venue.ga4_measurement_id;
    if (ga && !window.__ga4Loaded) {
      window.__ga4Loaded = true;
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga)}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", ga);
    }
    const px = venue.meta_pixel_id;
    if (px && !window.__fbqLoaded) {
      window.__fbqLoaded = true;
      /* eslint-disable */
      !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
      window.fbq("init", px);
      window.fbq("track", "PageView");
    }
  }, [venue]);
  // FOXID selfie / face-match — its own step after the licence. `selfieDone`
  // flips true once the member has taken (or skipped) the selfie. scanRef is
  // (re)set from the selfie response so a manual-entry selfie still links on
  // register.
  const [selfieDone, setSelfieDone] = useState(false);

  const sessionId    = useRef((typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2));
  const stepStart    = useRef(Date.now());

  /* ── Load venue + products + marketing options ── */
  useEffect(() => {
    (async () => {
      try {
        const info = await publicApi.getVenueInfo(slug);
        setVenue(info);
        if (info.stripePublishableKey) setStripePromise(loadStripe(info.stripePublishableKey));
        const [prods, opts] = await Promise.all([
          publicApi.getProducts(slug),
          publicApi.getMarketingOptions(slug).catch(() => []),
        ]);
        setProducts(prods);
        setMarketingOptions(Array.isArray(opts) ? opts : []);
      } catch {
        setError("Venue not found or not configured.");
      } finally {
        setLoadingVenue(false);
      }
    })();
  }, [slug]);

  /* ── Branding ──────────────────────────── */
  useEffect(() => {
    if (!venue) return;
    const r = document.documentElement.style;
    const primary = venue.primary_color || "#3b82f6";
    const accent = venue.accent_color || venue.primary_color || "#1e293b";
    const CARD_BG = "#ffffff"; // .signup-card is always a light surface
    r.setProperty("--su-primary",  primary);
    r.setProperty("--su-accent",   accent);
    r.setProperty("--su-bg",       venue.background_color  || "#f0f4f8");
    // Body/heading text sits on the white card — keep the venue's colour only
    // when it's legible there, else fall back to dark ink so headings never
    // wash out (a dark-theme text_color on a light card is the failure case).
    r.setProperty("--su-text",     readableOn(CARD_BG, venue.text_color, 4.5));
    // Button label sits on the primary colour — respect the venue's choice only
    // when it clears normal-text AA (4.5:1), otherwise auto-pick black/white.
    r.setProperty("--su-btn-text", readableOn(primary, venue.button_text_color, 4.5));
    // Contrast-safe BRAND colour for foreground use (prices, links, icons, the
    // active step label) on the white card — keeps the hue but darkens a pale/
    // bright primary until it's legible. Fills/buttons keep the raw --su-primary.
    r.setProperty("--su-primary-ink", inkOn(CARD_BG, primary, 4.5));
    // Legible ink for the header, which sits on the (admin-chosen) accent colour.
    r.setProperty("--su-header-ink", autoInk(accent));
    r.setProperty("--su-btn-radius", `${venue.button_radius ?? 10}px`);
    r.setProperty("--su-font",     venue.font_family ? `'${venue.font_family}', sans-serif` : "'Inter', sans-serif");
  }, [venue]);

  /* ── Branding preview bridge ─────────────────────
   * When embedded in the admin Branding page (…?brandPreview=1), accept a draft
   * venue over postMessage and apply it live, and tell the parent we're ready
   * once loaded so it sends the current draft. Same-origin, so '*' is fine. */
  useEffect(() => {
    if (!isBrandPreview) return undefined;
    const onMsg = (e) => {
      if (e.data && e.data.type === "suBrandPreview" && e.data.venue) {
        setVenue((cur) => (cur ? { ...cur, ...e.data.venue } : cur));
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [isBrandPreview]);
  useEffect(() => {
    if (isBrandPreview && !loadingVenue) {
      try { window.parent && window.parent.postMessage({ type: "suBrandPreviewReady" }, "*"); } catch { /* ignore */ }
    }
  }, [isBrandPreview, loadingVenue]);

  /* ── Reset scroll on step change ────────
   *
   * Without this, navigating from a long step (Personal Details) to a
   * shorter one (Terms, Comms, Plan) leaves the page scrolled to
   * wherever the previous step's submit button was — so on mobile the
   * new step opens half-way down the screen instead of at its title.
   * Run before paint so the user never sees the wrong position. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [step]);

  /* ── Mobile keyboard handling ───────────
   *
   * On-screen keyboards overlay the bottom of the screen and hide the
   * field being typed into. The window is the scroller on mobile (the card
   * is min-height:100dvh with no inner overflow), so on focus we lift the
   * field into the upper part of the screen, clear of any bottom keyboard.
   *
   * This is POSITION-based, not keyboard-detection based, on purpose:
   * device emulators (DevTools device mode) and in-app/webview previews
   * draw a keyboard WITHOUT resizing visualViewport, so measuring the
   * keyboard height is unreliable and would make us wrongly conclude "no
   * keyboard" and skip the scroll. Moving the focused field near the top
   * works regardless of whether the keyboard is detectable. Gated to mobile
   * widths so desktop scrolling is never disturbed. The matching CSS
   * reserves a screenful of scroll room while a field is focused
   * (`:focus-within`) so even the last field can lift clear. `--su-keyboard`
   * is still published for precise spacing where the viewport does resize. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    const root = document.documentElement;

    const syncInset = () => {
      const kb = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0;
      root.style.setProperty("--su-keyboard", `${kb}px`);
    };

    // Only keyboard-driven inputs need lifting. Date/time/picker types
    // (date, datetime-local, month, time, week) open a NATIVE PICKER, not a
    // keyboard — lifting them just shoves the page up and detaches the picker.
    const TYPEABLE = /^(?:text|email|tel|number|password|search|url)$/i;
    const isTypeable = (el) =>
      !!el && (el.tagName === "TEXTAREA" || el.isContentEditable ||
        (el.tagName === "INPUT" && TYPEABLE.test(el.getAttribute("type") || "text")));

    // Lift the focused field toward the top so a bottom keyboard can't
    // cover it. Mobile widths only, and only when the field sits low enough
    // to be at risk — fields already near the top are left in place.
    const liftIntoView = (el) => {
      if (!el || !el.isConnected) return;
      if (window.innerWidth > 699) return;            // desktop — leave scroll alone
      const ih = window.innerHeight;
      const rect = el.getBoundingClientRect();
      const desired = Math.min(120, ih * 0.16);       // target distance from top
      if (rect.top > ih * 0.35) {
        window.scrollBy({ top: rect.top - desired, behavior: "smooth" });
      }
    };

    let timer;
    const onFocusIn = (e) => {
      const el = e.target;
      if (!isTypeable(el) || !el.closest?.(".signup-card")) return;
      clearTimeout(timer);
      // Let the keyboard begin animating in before we scroll.
      timer = setTimeout(() => { syncInset(); liftIntoView(el); }, 280);
    };

    // On real devices the keyboard resizes the viewport — re-seat the
    // active field once that settles (refines the emulator-friendly lift).
    const onViewport = () => {
      syncInset();
      const el = document.activeElement;
      if (isTypeable(el) && el.closest?.(".signup-card")) liftIntoView(el);
    };

    document.addEventListener("focusin", onFocusIn);
    vv?.addEventListener("resize", onViewport);
    vv?.addEventListener("scroll", syncInset);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("focusin", onFocusIn);
      vv?.removeEventListener("resize", onViewport);
      vv?.removeEventListener("scroll", syncInset);
      root.style.removeProperty("--su-keyboard");
    };
  }, []);

  /* ── Take over body background with the venue's branded color ── */
  useEffect(() => {
    if (!venue) return;
    const bg = venue.background_color || "#f0f4f8";
    const prevBody = document.body.style.background;
    const prevHtml = document.documentElement.style.background;
    document.body.style.background = bg;
    document.documentElement.style.background = bg;
    return () => {
      document.body.style.background = prevBody;
      document.documentElement.style.background = prevHtml;
    };
  }, [venue]);

  /* ── Timing helper ─────────────────────── */
  const trackStep = (name, completed = true) => {
    publicApi.trackTiming(slug, {
      session_id:  sessionId.current,
      step_name:   name,
      duration_ms: Date.now() - stepStart.current,
      completed,
    }).catch(() => {});
    stepStart.current = Date.now();
  };

  /* ── Handlers ──────────────────────────── */
  const handleChoice = (existing) => {
    trackStep("choice");
    setIsExisting(existing);
    setLicenceDone(false);
    setSelfieDone(false);
    setScanRef(null);
    setStep(existing ? 1 : 2);
  };

  const handleLookupResult = (found, member, extras) => {
    trackStep("lookup");
    if (found && member) {
      setExistingMemberNumber(member.MEMBERNUMBER || null);
      setFormData({
        firstName:     member.FIRSTNAME || "",
        lastName:      member.SURNAME || "",
        title:         member.TITLE || "",
        dateOfBirth:   member.DATEOFBIRTH?.split("T")[0] || "",
        gender:        member.GENDER || "",
        telephone:     member.PRIMARYPHONE || "",
        mobile:        member.MOBILEPHONE || "",
        email:         member.EMAILADDRESS || "",
        streetAddress: member.ADDRESSLINE1 || "",
        suburb:        member.ADDRESSLINE2 || "",
        state:         member.ADDRESSLINE3 || "",
        postCode:      member.ADDRESSPOSTCODE || "",
        occupation:    member.OCCUPATION || "",
      });
      // Seed downstream pre-fills (currently just comms-prefs). Lookup
      // by name+DOB never sends a PIN, so extras may be absent there.
      if (extras?.commsPrefs) setCommsPrefs(extras.commsPrefs);
    } else {
      // Lookup miss (either by number or by name+DOB) — convert this
      // session to a new-member signup. Clearing `isExisting` makes
      // StepDetails show the new-member copy instead of "we found your
      // record", and keeps `isRenewal` false on register-member.
      setExistingMemberNumber(null);
      setIsExisting(false);
    }
    setLicenceDone(false);
    setSelfieDone(false);
    setScanRef(null);
    setStep(2);
  };

  // A venue can force-hide the whole Comms step; otherwise it shows when there
  // are marketing channels to offer or an AGM acknowledgement to collect.
  const commsEnabled = (venue?.comms_step_enabled !== false)
    && ((marketingOptions?.length > 0) || !!venue?.agm_enabled);
  const termsEnabled = !!venue?.terms_required;
  // FOXID licence capture is shown (in place of Details, until done) when the
  // venue has it ready AND enabled for this flow (signup vs renewal).
  const licenceEnabledForFlow = !!venue?.foxid_enabled
    && (isExisting ? !!venue?.foxid_on_renewal : !!venue?.foxid_on_signup);
  // Face match adds a selfie step right after the licence capture. Only when the
  // venue turned it on AND licence capture is active for this flow (it compares
  // the selfie to the licence photo, so a licence must be captured first).
  const faceMatchEnabledForFlow = licenceEnabledForFlow && !!venue?.foxid_face_match;

  // Step targeting helper — each handler picks the next *active* step
  // based on the venue's enabled features rather than chaining ifs.
  const nextStepAfter = (current) => {
    if (current === 2) return commsEnabled ? 3 : (termsEnabled ? 4 : 5);
    if (current === 3) return termsEnabled ? 4 : 5;
    if (current === 4) return 5;
    return current + 1;
  };

  // Licence scanned — merge the extracted fields into the form and swap to
  // the (now pre-filled) Details step. The member still reviews/edits before
  // continuing, since OCR isn't perfect.
  const handleScanned = (fields, ref) => {
    trackStep("licence");
    setFormData((prev) => ({ ...prev, ...fields }));
    setScanRef(ref || null);
    setLicenceDone(true);
  };
  const handleLicenceSkip = () => {
    trackStep("licence", false);
    setLicenceDone(true);
  };
  // Selfie step done — adopt the scan_ref the backend used (a manual-entry
  // selfie gets a fresh one) so register-member links the right document.
  const handleSelfieDone = (ref) => {
    if (ref) setScanRef(ref);
    setSelfieDone(true);
  };

  const handleDetailsSubmit = async (data) => {
    setFormData(data);
    setDuplicateInfo(null);
    // New-join duplicate guard (pre-payment). Only for new joins on
    // opted-in venues. Fail open — a check error never blocks the join.
    if (!isExisting && venue?.duplicate_check_enabled) {
      try {
        const r = await publicApi.checkDuplicate(slug, {
          firstName: data.firstName, lastName: data.lastName, dob: data.dateOfBirth,
        });
        if (r?.duplicate) {
          trackStep("details", false);
          setDuplicateInfo(r);
          if (typeof window !== "undefined") window.scrollTo({ top: 0, left: 0, behavior: "auto" });
          return;
        }
      } catch { /* fail open */ }
    }
    trackStep("details");
    setStep(nextStepAfter(2));
  };

  const handleCommsSubmit = (prefs) => {
    trackStep("comms");
    setCommsPrefs(prefs);
    setStep(nextStepAfter(3));
  };

  const handleTermsAccept = (acceptance) => {
    trackStep("terms");
    setTermsAcceptance(acceptance);
    setStep(nextStepAfter(4));
  };

  const handlePlanSelect = async (product) => {
    trackStep("plan");
    try {
      setSelectedProduct(product);
      const { clientSecret: cs } = await publicApi.createPaymentIntent(slug, {
        amount:     product.price_cents,
        currency:   product.currency || "aud",
        email:      formData.email,
        memberName: `${formData.firstName} ${formData.lastName}`,
        productId:  product.id,
      });
      setClientSecret(cs);
      setStep(6);
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    trackStep("payment");
    try {
      const res = await publicApi.registerMember(slug, {
        memberData: {
          ...formData,
          // also map to old field names for backward compat
          address1: formData.streetAddress || "",
          address2: formData.suburb ? `${formData.suburb}${formData.state ? " " + formData.state : ""}` : "",
          address3: "",
          // IGT identification passthrough
          identificationType: formData.identificationType || "",
          identificationNumber: formData.identificationNumber || "",
          identificationCountryId: formData.identificationCountryId || "",
          identificationState: formData.identificationState || "",
          identificationExpiry: formData.identificationExpiry || "",
          countryId: formData.countryId || "",
        },
        paymentIntentId,
        duration_months: selectedProduct.duration_months || 12,
        productId:       selectedProduct.id,
        amountCents:     selectedProduct.price_cents,
        isExisting,
        existingMemberNumber: existingMemberNumber || undefined,
        termsAcceptance: termsAcceptance || undefined,
        commsPrefs: commsPrefs || undefined,
        scanRef: scanRef || undefined,
        tracking: Object.keys(tracking).length ? tracking : undefined,
      });
      setResult(res);
      // Per-venue conversion events (B) — only fire if the venue configured a pixel.
      try {
        const value = (selectedProduct?.price_cents || 0) / 100;
        if (window.gtag && venue?.ga4_measurement_id) {
          window.gtag("event", "sign_up", { method: "web", value, currency: "AUD", transaction_id: res?.membershipId });
        }
        if (window.fbq && venue?.meta_pixel_id) {
          window.fbq("track", "CompleteRegistration", { value, currency: "AUD" });
        }
      } catch { /* analytics must never block the success screen */ }
      setStep(7);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReset = () => {
    setStep(0); setFormData({}); setSelectedProduct(null);
    setClientSecret(null); setResult(null); setIsExisting(false); setExistingMemberNumber(null); setError("");
    setTermsAcceptance(null);
    setCommsPrefs(null);
    setDuplicateInfo(null);
    setLicenceDone(false);
    setSelfieDone(false);
    setScanRef(null);
    sessionId.current = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    stepStart.current = Date.now();
  };

  /* ── Loading / error states ───────────── */
  if (loadingVenue) {
    return (
      <div className="signup-page">
        <div className="signup-mini" style={{ textAlign: "center", color: "var(--text-muted)" }}>Loading…</div>
      </div>
    );
  }
  if (error && !venue) {
    return (
      <div className="signup-page">
        <div className="signup-mini">
          <div className="su-alert error">{error}</div>
        </div>
      </div>
    );
  }

  const PROGRESS_STEPS = buildProgressSteps({ commsEnabled, termsEnabled });
  const pi = progressIndex(step, PROGRESS_STEPS);
  const showProgress = step >= 1 && step < 7;
  const heroBenefits = resolveBenefits(venue);
  const layout = resolveLayout(venue);

  return (
    <div className="signup-page">
      {/* Sandbox indicator — refined pill, shown only when the venue is in
          sandbox mode so testers know this isn't a live signup. */}
      {venue?.signup_mode === "sandbox" && (
        <div className="signup-testbar" role="status">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
          <span><strong>Test mode</strong> — sandbox payments &amp; records, not for real signups.</span>
        </div>
      )}

      <div className={shellClasses(layout)}>
        {/* ── Brand hero (desktop side panel / mobile top band) — the venue can
            hide it entirely for a clean single-column signup. ── */}
        {layout.heroEnabled && (
        <aside className="signup-hero">
          {venue?.cover_image_url && (
            <div className="signup-hero-cover" style={{ backgroundImage: `url(${venue.cover_image_url})`, opacity: layout.heroCoverOpacity }} aria-hidden="true" />
          )}
          <div className="signup-hero-top">
            {layout.heroShowLogo && venue?.logo_url && (
              <div className="signup-hero-logo">
                <img src={venue.logo_url} alt={venue.name} onError={(e) => { e.target.parentElement.style.display = "none"; }} />
              </div>
            )}
            {isSlotVisible(venue, "welcome_title") && (
              <h1 className="signup-hero-title" style={slotCss(venue, "welcome_title")}>{venue?.welcome_title || venue?.name || "Membership"}</h1>
            )}
            {venue?.welcome_subtitle && isSlotVisible(venue, "welcome_subtitle") && (
              <p className="signup-hero-sub" style={slotCss(venue, "welcome_subtitle")}>{venue.welcome_subtitle}</p>
            )}
          </div>
          {layout.heroShowBenefits && heroBenefits.length > 0 && (
            <ul className="signup-hero-props">
              {heroBenefits.map((b, i) => (
                <li key={i}>
                  <span className="signup-hero-ico"><BenefitIcon name={b.icon} size={16} /></span>
                  {b.label}
                </li>
              ))}
            </ul>
          )}
        </aside>
        )}

        {/* ── Form panel ── */}
        <div className="signup-card">
          {/* Hero hidden → keep a compact branded header on the card so the
              form still reads as on-brand rather than a bare white panel.
              Colour is left to CSS (legible dark ink on white) even if the
              venue set a light hero-title colour, so it never washes out. */}
          {!layout.heroEnabled && (venue?.logo_url || isSlotVisible(venue, "welcome_title") || (venue?.welcome_subtitle && isSlotVisible(venue, "welcome_subtitle"))) && (
            <div className="signup-cardbrand">
              {venue?.logo_url && (
                <div className="signup-cardbrand-logo">
                  <img src={venue.logo_url} alt={venue.name} onError={(e) => { e.target.parentElement.style.display = "none"; }} />
                </div>
              )}
              {isSlotVisible(venue, "welcome_title") && (
                <h1 className="signup-cardbrand-title" style={{ ...slotCss(venue, "welcome_title"), color: undefined }}>{venue?.welcome_title || venue?.name || "Membership"}</h1>
              )}
              {venue?.welcome_subtitle && isSlotVisible(venue, "welcome_subtitle") && (
                <p className="signup-cardbrand-sub" style={{ ...slotCss(venue, "welcome_subtitle"), color: undefined }}>{venue.welcome_subtitle}</p>
              )}
            </div>
          )}

        {/* Progress bar */}
        {showProgress && (
          <div className="signup-progress">
            <div className="progress-steps">
              {PROGRESS_STEPS.map((s, i) => (
                <React.Fragment key={s.key}>
                  {i > 0 && <div className={`progress-step-line ${i <= pi ? "done" : ""}`} />}
                  <div
                    className={`progress-step ${i === pi ? "active" : ""} ${i < pi ? "done" : ""}`}
                    aria-current={i === pi ? "step" : undefined}
                    aria-label={`${s.label} — step ${i + 1} of ${PROGRESS_STEPS.length}`}
                  >
                    <div className="progress-dot" aria-hidden="true">{i < pi ? "✓" : i + 1}</div>
                    <div className="progress-step-label">{s.label}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        <div className="step-body">
          {error && step < 7 && <div className="su-alert error">⚠ {error}</div>}

          {step === 0 && <StepChoice venue={venue} onChoice={handleChoice} />}

          {step === 1 && (
            <StepLookup slug={slug} venue={venue} onResult={handleLookupResult} onBack={() => setStep(0)} />
          )}

          {step === 2 && (duplicateInfo ? (
            <DuplicateNotice
              info={duplicateInfo}
              onEdit={() => setDuplicateInfo(null)}
            />
          ) : (licenceEnabledForFlow && !licenceDone) ? (
            <StepLicence
              slug={slug}
              venue={venue}
              isExisting={isExisting}
              onScanned={handleScanned}
              onSkip={handleLicenceSkip}
              onBack={() => setStep(isExisting ? 1 : 0)}
            />
          ) : (faceMatchEnabledForFlow && !selfieDone) ? (
            <StepSelfie
              slug={slug}
              isExisting={isExisting}
              scanRef={scanRef}
              onDone={handleSelfieDone}
              onBack={() => setLicenceDone(false)}
            />
          ) : (
            <StepDetails
              slug={slug}
              venue={venue}
              initialData={formData}
              isExisting={isExisting}
              onSubmit={handleDetailsSubmit}
              onBack={() => {
                if (faceMatchEnabledForFlow) setSelfieDone(false);
                else if (licenceEnabledForFlow) setLicenceDone(false);
                else setStep(isExisting ? 1 : 0);
              }}
            />
          ))}

          {step === 3 && commsEnabled && (
            <StepCommsPrefs
              venue={venue}
              marketingOptions={marketingOptions}
              initialPrefs={commsPrefs}
              onSubmit={handleCommsSubmit}
              onBack={() => setStep(2)}
            />
          )}

          {step === 4 && termsEnabled && (
            <StepTerms
              venue={venue}
              initialAcceptance={termsAcceptance}
              onAccept={handleTermsAccept}
              onBack={() => setStep(commsEnabled ? 3 : 2)}
            />
          )}

          {step === 5 && (
            <StepPlan
              products={products}
              onSelect={handlePlanSelect}
              onBack={() => setStep(termsEnabled ? 4 : (commsEnabled ? 3 : 2))}
            />
          )}

          {step === 6 && clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <StepPayment
                selectedProduct={selectedProduct}
                sandbox={venue?.signup_mode === "sandbox"}
                slug={slug}
                paymentIntentId={clientSecret ? clientSecret.split("_secret")[0] : null}
                onSuccess={handlePaymentSuccess}
                onBack={() => setStep(5)}
              />
            </Elements>
          )}

          {step === 7 && <StepConfirmation result={result} isExisting={isExisting} onStartOver={handleReset} />}
        </div>
        </div>{/* .signup-card */}
      </div>{/* .signup-shell */}
    </div>
  );
}

/* Shown in place of the Details step when the new-join duplicate guard
 * matches an existing member on name + DOB. Offers a one-tap jump to the
 * renewal flow (where they enter their membership number) and a way back
 * to edit details in case they mistyped. */
function DuplicateNotice({ info, onEdit }) {
  // A new signup that matches an existing member is a hard stop here — we
  // never steer to renewal (sign up is sign up), so the only action is to
  // go back and fix a possible typo. The backend message points the member
  // to staff to check their membership status.
  const message = info?.message
    || "You are already a Member! Please visit the club and speak to staff to check the status of your membership.";
  return (
    <>
      <h2 className="step-title">You're already a member</h2>
      <div className="su-alert info" style={{ flexDirection: "column", alignItems: "flex-start", gap: "0.5rem" }}>
        <span>{message}</span>
      </div>
      <div className="su-btn-row">
        <button type="button" className="su-btn su-btn-outline" onClick={onEdit}>← Edit details</button>
      </div>
    </>
  );
}
